import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Fab, Modal } from "@mui/material";
import { useParams } from "react-router-dom";
import DebriefingVideo from "./debriefing-video/DebriefingVideo";
import DebriefingChecklistView from "./debriefing-checklist/DebriefingChecklistView";
import DebriefingService from "../../services/DebriefingService";
import CheckListService from "../../services/CheckListService";
import StationsService from "../../services/StationsService";
import ScenarioService from "../../services/ScenarioService";
import ChecklistIcon from "@mui/icons-material/Checklist";

export default function DebriefingFormExternal() {
  // Busca os parâmetros diretamente da URL
  const params = useParams();
  const eventId = params.eventId;
  const stationId = params.stationId;
  const evaluatedId = params.evaluatedId;
  const debriefingId = params.debriefingId;

  // Estados para dados necessários
  const [station, setStation] = useState<any>(null);
  const [scenario, setScenario] = useState<any>(null);
  const [checklistId, setChecklistId] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [video, setVideo] = useState("");
  const [videoId, setVideoId] = useState("");
  const [arrVideo, setArrVideo] = useState<any[]>([]);
  const [cameraMain, setCameraMain] = useState(false);
  const [cameraIndex, setCameraIndex] = useState("1");
  const [checklistResult, setChecklistResult] = useState({});
  const [openChecklist, setOpenChecklist] = useState(false);

  // Carrega dados iniciais da estação, cenário e checklist
  useEffect(() => {
    async function fetchInitialData() {
      setLoading(true);
      try {
        // Busca dados da estação
        const stationResp = await StationsService.get(stationId);
        setStation(stationResp.data);
        setChecklistId(stationResp.data.checklist_id);
        // Busca dados do cenário
        if (stationResp.data.scenario_id) {
          const scenarioResp = await ScenarioService.getScenarioById(stationResp.data.scenario_id);
          setScenario(scenarioResp.data);
        }
      } catch (e) {
        // erro silencioso
      }
      setLoading(false);
    }
    if (stationId) fetchInitialData();
  }, [stationId]);

  // Carrega vídeo e checklist
  useEffect(() => {
    if (checklistId && debriefingId) {
      loadDebriefingUrl(debriefingId);
      loadChecklistDebriefing(checklistId, debriefingId);
    }
  }, [checklistId, debriefingId]);

  const loadChecklistDebriefing = async (checklistId: any, debriefingId: any) => {
    await CheckListService.getChecklistDebriefing(checklistId, debriefingId)
      .then((response: any) => {
        if (response) {
          setChecklistResult(response.data);
        }
      })
      .catch(() => {});
  };

  const loadDebriefingUrl = async (debriefingId: any) => {
    await DebriefingService.getDebriefingUrl(debriefingId)
      .then((response: any) => {
        if (response) {
          if (
            response.data.length > 0 &&
            response.data[0].field_name !== "debriefing_video"
          ) {
            setVideo(response.data[0]?.recording_url);
            setVideoId(response.data[0]?.streaming_id);
            const cameraDefault = response.data[0]?.camera_main ? true : false;
            setCameraMain(cameraDefault);
            setArrVideo(response.data);
            setCameraIndex("1");
          } else {
            setVideo(response.data[0]?.static_url);
            setVideoId(response.data[0]?.id);
            setCameraMain(true);
            setCameraIndex("1");
          }
        }
      })
      .catch(() => {});
  };

  if (loading) {
    return (
      <Box sx={{ width: "100vw", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100vw",
        minHeight: "100vh",
        bgcolor: "background.default",
        m: 0,
        p: 0,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Video fixo no topo */}
      <Box
        sx={{
          width: "100%",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 10,
          background: "#fff",
          maxHeight: '75vh', // altura máxima do vídeo
          height: { xs: '56vw', sm: '40vw', md: '32vw', lg: '28vw', xl: '28vw' },
          minHeight: 200,
          boxShadow: 2,
        }}
      >
        <DebriefingVideo
          video={video}
          videoId={videoId}
          arrVideo={arrVideo}
          setVideo={setVideo}
          setVideoId={setVideoId}
          cameraMain={cameraMain}
          setCameraMain={setCameraMain}
          cameraIndex={cameraIndex}
          setCameraIndex={setCameraIndex}
          isExternal={true}
          heightVideo="100%"
        />
      </Box>
      {/* Botão flutuante para abrir o checklist */}
      <Fab
        color="primary"
        aria-label="Abrir checklist"
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 20,
        }}
        onClick={() => setOpenChecklist(true)}
      >
        <ChecklistIcon />
      </Fab>
      {/* Modal do checklist */}
      <Modal
        open={openChecklist}
        onClose={() => setOpenChecklist(false)}
        aria-labelledby="modal-checklist-title"
        aria-describedby="modal-checklist-desc"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: '95vw', sm: 600, md: 700 },
            maxHeight: '90vh',
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 2,
            overflowY: "auto",
          }}
        >
          {/* Botão flutuante para fechar o modal */}
          <Fab
            color="secondary"
            size="small"
            aria-label="Fechar checklist"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 30,
            }}
            onClick={() => setOpenChecklist(false)}
          >
            <span style={{ fontWeight: 'bold', fontSize: 18 }}>&times;</span>
          </Fab>
          <DebriefingChecklistView
            checklistId={checklistId}
            eventId={eventId}
            stationId={stationId}
            evaluatedId={evaluatedId}
            debriefingId={debriefingId}
            station={station}
            scenario={scenario}
            checklistResult={checklistResult}
            loadChecklistDebriefing={loadChecklistDebriefing}
          />
        </Box>
      </Modal>
    </Box>
  );
}
